import { create } from 'zustand';
import {
  configureAuth,
  signIn as apiSignIn,
  signOut as apiSignOut,
  getCurrentUser,
  isAuthenticated,
  completeNewPassword as apiCompleteNewPassword,
  type AuthUser,
} from '@acitra/api-client';
import { loadConfig } from '../config';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  needsNewPassword: boolean;
}

interface AuthActions {
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<boolean>;
  clearError: () => void;
}

type AuthStore = AuthState & AuthActions;

let authConfigured = false;

export const useAuthStore = create<AuthStore>((set) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  needsNewPassword: false,

  // Initialize - load config, configure auth, check if user is authenticated
  initialize: async () => {
    try {
      set({ isLoading: true, error: null });

      // Load runtime config and configure auth (only once)
      if (!authConfigured) {
        const config = await loadConfig();
        if (config.cognitoUserPoolId && config.cognitoClientId) {
          configureAuth({
            userPoolId: config.cognitoUserPoolId,
            userPoolClientId: config.cognitoClientId,
          });
          authConfigured = true;
        } else {
          // No auth config available
          set({ user: null, isAuthenticated: false, isLoading: false });
          return;
        }
      }

      const authenticated = await isAuthenticated();
      if (authenticated) {
        const user = await getCurrentUser();
        set({ user, isAuthenticated: true, isLoading: false });
      } else {
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  // Login with email and password
  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null, needsNewPassword: false });

      const result = await apiSignIn(email, password);

      // Check if user needs to set new password (first login)
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        set({ isLoading: false, needsNewPassword: true });
        return false;
      }

      if (result.isSignedIn) {
        const user = await getCurrentUser();
        set({ user, isAuthenticated: true, isLoading: false });
        return true;
      }

      set({ isLoading: false });
      return false;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      set({ error: message, isLoading: false });
      return false;
    }
  },

  // Complete new password challenge (first login)
  completeNewPassword: async (newPassword: string) => {
    try {
      set({ isLoading: true, error: null });

      const result = await apiCompleteNewPassword(newPassword);

      if (result.isSignedIn) {
        const user = await getCurrentUser();
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          needsNewPassword: false,
        });
        return true;
      }

      set({ isLoading: false });
      return false;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password change failed';
      set({ error: message, isLoading: false });
      return false;
    }
  },

  // Logout
  logout: async () => {
    try {
      await apiSignOut();
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        error: null,
        needsNewPassword: false,
      });
    }
  },

  // Clear error
  clearError: () => set({ error: null }),
}));
